import './App.css';
import '@fortawesome/fontawesome-svg-core/styles.css';
import AuthLayout from './layouts/auth-layout';
import { BrowserRouter, Routes,Route, Navigate, useLocation, useParams } from 'react-router-dom';
import ProtectedRoutes from './utilities/protected-routes';
import SetPassword from './auth/setpassword';
import ActivateAccount from './auth/activate-account';
import PasskeySecurity from './auth/passkey-security';
import ManageUsers from './pages/admin/manageusers';
import useApiUrlStore from './stores/api-url-store';
import ViewReports from './pages/reports/viewreports';
import ClientSearch from './pages/Client/ClientSearch';
import HomePage from './pages/home/homepage';
import ManageRoles from './pages/admin/manageroles';
import AuditLogs from './pages/admin/auditlogs';
import ErrorLogs from './pages/admin/errorlogs';
import ImpersonateUser from './pages/admin/impersonateuser';
import ManageGroupAppNew from './pages/admin/managegroupappnew';
import {  QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './new/queryClient';
import NotFound from './pages/notfound/notfound';
import PublicOnlyRoutes from './utilities/public-only-routes';
import ClientLandingPageNew from './pages/Client/ClientLandingPageNew';
import ClientFormsPage from './pages/Client/ClientFormsPage';
import ApplicationFormNew from './pages/Client/forms/applicationformnew';
import PDS from './pages/Client/forms/pds/pds';
import ScreeningForm from './pages/Client/forms/screeningform';
import FaceSheet from './pages/Client/forms/facesheet';
import DocumentList from './components/documentlist';
import TransitionPlanMain from './pages/Client/forms/transitionplan/transitionplanmain';
import CostEstimate from './pages/Client/forms/costestimate/costestimate';
import ChangeAppStatusNew from './pages/Client/forms/changeapplicationstatus/changeappstatusnew';
import HousingNew from './pages/Client/forms/housing/housingnew';
import TransitionCheckList from './pages/Client/forms/transitionchecklist/transitionchecklist';
import ClientApplicationHistory from './pages/Client/ClientApplicationHistory';
import ExpenditureWorksheet from './pages/Client/forms/expenditureworksheet/expenditureworksheet';
import Notes from './pages/Client/forms/notes';
import ClientApplications from './components/clientapplications';
import IncidentsList from './pages/Client/forms/incidentsnew/incidentslist';
import IncidentWizardNew from './pages/Client/forms/incidentsnew/incidentwizardnew';
import RestoreScroll from './components/restorescroll';
import { useEffect } from 'react';
import { resolveApiBaseUrl } from './new/api-base-url';
import SessionExpiredModal from './components/session-expired-modal';
import AuthSessionBootstrap from './components/auth-session-bootstrap';
import { getApplicationLandingPath, hasPermission, permissionPaths } from './auth/permission-paths';
import useUserStore from './stores/user-store';
import StagingIndicator from './components/staging-indicator';

function LegacyAccountSecurityRedirect() {
  const location = useLocation();

  return <Navigate to={`/Account/Settings${location.search}`} replace />;
}

function ApplicationIndexRoute() {
  const { applicationid } = useParams();
  const permissions = useUserStore((state) => state.permissions);

  if (hasPermission(permissions, permissionPaths.applicationForm.display)) {
    return <ApplicationFormNew />;
  }

  const landingPath = getApplicationLandingPath(permissions, applicationid);

  return landingPath
    ? <Navigate to={landingPath} replace />
    : <Navigate to="/404" replace />;
}

function App() {


  const setBaseUrl = useApiUrlStore((state) => state.setBaseUrl);

  useEffect(() => {
    setBaseUrl(resolveApiBaseUrl());
  }, [setBaseUrl]);

  return (
    <QueryClientProvider client={queryClient}>
      <StagingIndicator />
      <BrowserRouter>
        <AuthSessionBootstrap>
          <RestoreScroll />
          <SessionExpiredModal />
          <Routes>
                <Route path="/Auth/Login" element={<PublicOnlyRoutes><AuthLayout /></PublicOnlyRoutes>} />
                <Route path="/" element={<ProtectedRoutes><HomePage /></ProtectedRoutes>} />
                <Route path="/Auth/ActivateAccount" element={<ActivateAccount />} />
                <Route path="/Auth/SetPassword" element={<PublicOnlyRoutes><SetPassword /></PublicOnlyRoutes>} />
                <Route path="/Account/Settings" element={<ProtectedRoutes><PasskeySecurity /></ProtectedRoutes>} />
                <Route path="/Account/Security" element={<ProtectedRoutes><LegacyAccountSecurityRedirect /></ProtectedRoutes>} />
                <Route path="/Client/ClientSearch" element={<ProtectedRoutes permission={permissionPaths.clientSearch.display}><ClientSearch /></ProtectedRoutes>} />
                <Route path="/Client/Client/:medicaidid" element={<ProtectedRoutes anyPermissions={[permissionPaths.clientApplications.display, permissionPaths.clientIncidents.display]}><ClientLandingPageNew /></ProtectedRoutes>}>
                  <Route index element={<ProtectedRoutes permission={permissionPaths.clientApplications.display}><ClientApplications /></ProtectedRoutes>} />
                  <Route path='incidents' element={<ProtectedRoutes permission={permissionPaths.clientIncidents.display}><IncidentsList /></ProtectedRoutes>} />
                  <Route path='incidents/add' element={<ProtectedRoutes permission={permissionPaths.clientIncidents.add}><IncidentWizardNew /></ProtectedRoutes>} />
                  <Route path='incidents/:id' element={<ProtectedRoutes permission={permissionPaths.clientIncidents.update}><IncidentWizardNew /></ProtectedRoutes>} />
                </Route>
                {/* <Route path="/Client/Incidents" element={<ProtectedRoutes><Incidents /></ProtectedRoutes>} /> */}
                <Route path="/Client/Applications/:applicationid" element={<ProtectedRoutes permission={permissionPaths.clientApplications.display}><ClientFormsPage /></ProtectedRoutes>}>
                  <Route index element={<ApplicationIndexRoute />} />
                  <Route path='screening' element={<ProtectedRoutes permission={permissionPaths.screening.display}><ScreeningForm /></ProtectedRoutes>} />
                  <Route path='facesheet' element={<ProtectedRoutes permission={permissionPaths.faceSheet.display}><FaceSheet /></ProtectedRoutes>} />
                  <Route path='documentupload' element={<ProtectedRoutes permission={permissionPaths.documents.display}><DocumentList /></ProtectedRoutes>} />
                  <Route path='costestimate' element={<ProtectedRoutes permission={permissionPaths.costEstimate.display}><CostEstimate /></ProtectedRoutes>} />
                  <Route path='transitionplan' element={<ProtectedRoutes permission={permissionPaths.transitionPlan.display}><TransitionPlanMain /></ProtectedRoutes>} />
                  <Route path='appstatus' element={<ProtectedRoutes permission={permissionPaths.applicationHistory.add}><ChangeAppStatusNew /></ProtectedRoutes>} />
                  <Route path='housing' element={<ProtectedRoutes permission={permissionPaths.housing.display}><HousingNew /></ProtectedRoutes>} />
                  <Route path='transitionchecklist' element={<ProtectedRoutes permission={permissionPaths.transitionChecklist.display}><TransitionCheckList /></ProtectedRoutes>} />
                  <Route path='history' element={<ProtectedRoutes permission={permissionPaths.applicationHistory.display}><ClientApplicationHistory /></ProtectedRoutes>} />
                  <Route path='expenditureworksheet' element={<ProtectedRoutes permission={permissionPaths.expenditureWorksheet.display}><ExpenditureWorksheet /></ProtectedRoutes>} />
                  <Route path='casemanagementnotes' element={
                    <ProtectedRoutes permission={permissionPaths.notes.caseManagementDisplay}>
                        <Notes
                          key={"a"}
                          noteLabel='Case Management Notes'
                          getAllEndpoint='CaseManagementNotes'
                          getSingleEndpoint='CaseManagementNote'
                          AddEndpoint='AddCaseManagementNote'
                          UpdateEndpoint='UpdateCaseManagementNote'
                          permissionbase='CaseManagementNotes'
                          afterHoursWorkFlow={true}
                      />
                    </ProtectedRoutes>
                  } />
                  <Route path='noteshousing' element={
                    <ProtectedRoutes permission={permissionPaths.notes.housingDisplay}>
                        <Notes
                          key={"b"}
                          noteLabel='Housing Notes'
                          getAllEndpoint='HousingNotes'
                          getSingleEndpoint='HousingNote'
                          AddEndpoint='AddHousingNote'
                          UpdateEndpoint='UpdateHousingNote'
                          permissionbase='HousingNotes'
                          afterHoursWorkFlow={false}
                      />
                    </ProtectedRoutes>
                  } />
                  <Route path='hdinotes' element={
                    <ProtectedRoutes permission={permissionPaths.notes.hdiDisplay}>
                        <Notes
                          key={"c"}
                          noteLabel='HDI Notes'
                          getAllEndpoint='HDINotes'
                          getSingleEndpoint='HDINote'
                          AddEndpoint='AddHDINote'
                          UpdateEndpoint='UpdateHDINote'
                          permissionbase='HDINotes'
                          afterHoursWorkFlow={false}
                      />
                    </ProtectedRoutes>
                  } />
                  <Route path='pds' element={<ProtectedRoutes permission={permissionPaths.participantDirectedServices.display}><PDS /></ProtectedRoutes>} />
                </Route>
                <Route path="/Admin/ManageUsers" element={<ProtectedRoutes permission={permissionPaths.administration.manageUsers}><ManageUsers /></ProtectedRoutes>} />
                <Route path="/Admin/ManageRoles" element={<ProtectedRoutes permission={permissionPaths.administration.manageRoles}><ManageRoles /></ProtectedRoutes>} />
                <Route path="/Admin/AuditLogs" element={<ProtectedRoutes permission={permissionPaths.administration.viewAuditLogs}><AuditLogs /></ProtectedRoutes>} />
                <Route path="/Admin/ErrorLogs" element={<ProtectedRoutes permission={permissionPaths.administration.viewErrorLogs}><ErrorLogs /></ProtectedRoutes>} />
                <Route path="/Admin/ManageGroupApp" element={<ProtectedRoutes permission={permissionPaths.applicationHistory.update}><ManageGroupAppNew /></ProtectedRoutes>} />
                <Route path="/Reports/ViewReports" element={<ProtectedRoutes permission={permissionPaths.reports.display}><ViewReports /></ProtectedRoutes>} />
                <Route path='/404' element={<NotFound />} />
                <Route path='*' element={<Navigate to='/404' />} />
          </Routes>
        </AuthSessionBootstrap>
        </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
